import { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { useResponsive } from '../../../hooks/useResponsive';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import {
  getMonthGridRange,
  getViewerTimeZone,
  isToday as isLocalToday,
  type LocalDate,
  localDateKey,
} from '../utils/calendarDate';
import { indexEventsByLocalDay } from '../utils/calendarEventBucketing';
import { CalendarMonthDayCell } from './CalendarMonthDayCell';

const WEEKDAY_LABELS_SUNDAY_FIRST = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// A fixed cap per breakpoint, not a true onLayout-measured "how many chips
// actually fit". A real measured cap needs a layout pass this project's
// jsdom test environment doesn't provide deterministically (no real Yoga/
// layout engine — onLayout never fires with real dimensions under jest), so
// asserting on it would mean mocking layout events rather than testing real
// behavior. A breakpoint tier is the cheap proxy for "available height" the
// brief allows when true measurement isn't cheap.
const MAX_VISIBLE_EVENTS: Record<'mobile' | 'tablet' | 'desktop' | 'wide', number> = {
  mobile: 2,
  tablet: 3,
  desktop: 3,
  wide: 4,
};

interface CalendarMonthGridProps {
  anchor: LocalDate;
  events: CalendarEvent[];
  timeZone?: string;
  weekStartsOn?: 0 | 1;
  /** Injectable for tests; defaults to the real current instant. */
  now?: Date;
  onSelectDay: (date: LocalDate) => void;
}

export function CalendarMonthGrid({
  anchor,
  events,
  timeZone = getViewerTimeZone(),
  weekStartsOn = 0,
  now = new Date(),
  onSelectDay,
}: Readonly<CalendarMonthGridProps>) {
  const { breakpoint } = useResponsive();
  const maxVisibleEvents = MAX_VISIBLE_EVENTS[breakpoint];

  const weeks = useMemo(
    () => getMonthGridRange(anchor.year, anchor.month, weekStartsOn),
    [anchor.year, anchor.month, weekStartsOn],
  );

  const eventsByDay = useMemo(() => indexEventsByLocalDay(events, timeZone), [events, timeZone]);

  const weekdayLabels =
    weekStartsOn === 1
      ? [...WEEKDAY_LABELS_SUNDAY_FIRST.slice(1), WEEKDAY_LABELS_SUNDAY_FIRST[0]]
      : WEEKDAY_LABELS_SUNDAY_FIRST;

  return (
    <Box testID="calendar_month_grid">
      {/* Uppercase eyebrow per the design system (11px / 500 / tracking /
          text.tertiary) — plain sentence-case labels read as body copy and
          competed with the day numbers underneath them. */}
      <Box flexDirection="row">
        {weekdayLabels.map((label) => (
          <Box key={label} flex={1} paddingY="sm" alignItems="center">
            <Text variant="label" color={colors.text.tertiary} style={styles.weekday}>
              {label.toUpperCase()}
            </Text>
          </Box>
        ))}
      </Box>

      {/* ONE bordered surface for the whole month, not 42. The cells draw only
          their right/bottom hairlines, so the outer frame closes the grid and
          the depth budget holds (canvas is never bordered; one border per
          branch). */}
      <Box style={styles.grid}>
        {weeks.map((week) => (
          <Box key={localDateKey(week[0])} style={styles.week}>
            {week.map((date) => (
              <CalendarMonthDayCell
                key={localDateKey(date)}
                date={date}
                isCurrentMonth={date.month === anchor.month && date.year === anchor.year}
                isToday={isLocalToday(date, timeZone, now)}
                events={eventsByDay.get(localDateKey(date)) ?? []}
                maxVisibleEvents={maxVisibleEvents}
                onPress={() => onSelectDay(date)}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create({
  weekday: {
    fontWeight: '500',
    letterSpacing: tokens.letterSpacing.wider,
  },
  grid: {
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.lg,
    overflow: 'hidden',
  },
  week: {
    flexDirection: 'row',
  },
});
